import { create } from 'zustand';

import { votesApi } from '@/lib/api';

// État partagé de la fenêtre de vote pour le badge nav (design-handoff item
// 8, report chantier 5 : « Badge sur Votes pendant la fenêtre » — rejoint le
// push VOTE_OPEN). L'API reste l'AUTORITÉ sur ouvert/fermé (ADR 0004 D2/D3) :
// ce store ne fait QUE relayer `votesApi.current()`, jamais recalculer la
// fenêtre côté client (même doctrine que useVoteWindow.ts). `isOpen` reste
// `null` tant qu'aucun fetch n'a abouti — le badge (BottomNav.tsx) ne
// s'affiche que sur `isOpen === true`, jamais sur un état inconnu.
const THROTTLE_MS = 5 * 60 * 1000;

interface VoteWindowState {
  isOpen: boolean | null;
  lastFetchedAt: number | null;
  refresh: () => Promise<void>;
}

export const useVoteWindowStore = create<VoteWindowState>((set, get) => ({
  isOpen: null,
  lastFetchedAt: null,
  refresh: async () => {
    const { lastFetchedAt } = get();
    const now = Date.now();
    if (lastFetchedAt !== null && now - lastFetchedAt < THROTTLE_MS) return;

    // Marqué AVANT l'appel réseau : le throttle protège l'API même quand
    // l'appel échoue — sinon un backend en panne ferait retenter ce store à
    // chaque montage de ClientLayout au lieu d'une fois toutes les 5 minutes.
    set({ lastFetchedAt: now });

    try {
      const voteState = await votesApi.current();
      set({ isOpen: voteState.state === 'open' });
    } catch {
      // Échec silencieux (offline, 5xx...) — `isOpen` reste tel quel (null
      // si jamais fetché avec succès) : jamais de badge affiché sur une
      // supposition, jamais d'erreur remontée pour un aparté d'affichage.
    }
  },
}));
